import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  AppState,
  AppStateStatus,
  FlatList,
  Keyboard,
  Platform,
  View
} from 'react-native';
import styled from 'styled-components/native';

import { Feather } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';

import IconButton from '../IconButton';
import ListItem from '../../components/list/ListItem';
import ListItemChatBubble from '../../components/list/ListItemChatBubble';
import ListItemIcon from '../../components/list/ListItemIcon';
import { Caption, SubCaption } from '../../components/Typography';
import Colors from '../../constants/Colors';
import { TextInput } from 'react-native-gesture-handler';
import { differenceInMinutes, format } from 'date-fns';
import { User } from '../../models/User';
import { useRoom } from '../../hooks/room/useRoom';
import { Message } from '../../models/Message';
import { useMemo } from 'react';
import ProfileButtonMenu from '../ProfileButtonMenu';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ImageActionSheet from '../modals/ImageActionSheet';
import * as ImagePicker from 'expo-image-picker';
import { log } from '../../tools/LogHelper';
import elementIDs from '../../../elementIDs';
import ActivityIndicatorScreen from '../ActivityIndicatorScreen';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { useSafeTabBarHeight } from '../../common/safeTabBarHeight';

const textFieldDefaultHeight = 44;
const chatMessageMaxChars = 500;

// all screen content must be in one KeyboardAvoidingView
const StyledContainer = styled(KeyboardAvoidingView)<{
  isKeyboardVisible?: boolean;
}>`
  display: flex;
  flex-direction: column;
  background-color: white;
  flex: 1 1 auto;
  margin-bottom: ${p => (p.isKeyboardVisible ? 94 : 0)}px;
`;

const StyledFlatList = styled(FlatList<Message>)`
  flex: 1;
`;

const StyledListItem = styled(ListItem)<{ left?: boolean }>`
  display: flex;
  justify-content: ${p => (p.left ? 'flex-start' : 'flex-end')};
  align-items: flex-end;
`;

const StyledTextInputContainer = styled.View`
  display: flex;
  flex-direction: row;
`;

const StyledComposerWrap = styled.View`
  flex-direction: column;
`;

const StyledComposerFooter = styled.View`
  display: flex;
  flex-direction: ${Platform.OS === 'web' ? 'column' : 'row-reverse'};
  width: 100%;
  padding: 0 28px 16px 28px;
  justify-content: ${Platform.OS === 'web' ? 'flex-end' : 'space-between'};
`;

const StyledLimitHint = styled(SubCaption)`
  text-align: right;
`;

const StyledTextInput = styled.TextInput`
  flex: 1;
  min-height: 44px;
  margin: 0 16px 12px 16px;
  padding: ${Platform.OS === 'android' ? 0 : 12}px 44px 0 16px
  border-width: 1px;
  border-color: rgba(102, 102, 102, 0.24);
  border-radius: 100px;
  max-height: 300px;
`;

const StyledIconButton = styled(IconButton)`
  height: 25px;
  width: 25px;
  position: absolute;
  bottom: 22px;
  right: 34px;
`;

const StyledMessageView = styled.View<{ rightAlign?: boolean }>`
  position: relative;
  flex: 1 1 auto;
  align-items: ${p => (p.rightAlign ? 'flex-end' : 'flex-start')};
`;

const StyledDateText = styled(Caption)`
  font-size: 12px;
  color: #999;
`;

const StyledChatNameText = styled(SubCaption)`
  color: #999;
  margin: 0 0 0 8px;
`;

const StyledArchiveTextContainer = styled.View`
  display: flex;
  align-items: center;
  padding: 10px;
`;

const ChatRoom: React.FC<{
  embedded?: boolean;
  user: User;
  roomId?: number;
  onTrackViewedMessages?: () => void;
}> = ({ embedded, user, roomId, onTrackViewedMessages }) => {
  const onTrackViewedMessagesRef = useRef(onTrackViewedMessages);
  onTrackViewedMessagesRef.current = onTrackViewedMessages;
  const [openImageActionSheet, setOpenImageActionSheet] = useState(false);
  const {
    getMessages,
    postMessage,
    trackViewedMessages,
    archived,
    canPost,
    ...room
  } = useRoom(user.id, roomId || 0);
  const flatListRef = useRef<FlatList>(null!);
  const textInputRef = useRef<TextInput>(null!);
  const lastAppliedInputHeightRef = useRef(textFieldDefaultHeight);
  const submitFocusTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const [text, setText] = React.useState('');
  const [textInputHeight, setTextInputHeight] = useState(44);
  const appState = useRef(AppState.currentState);
  const [loadingMessages, setLoadingMessages] = useState<boolean>();
  // get the room messages
  useFocusEffect(
    useCallback(() => {
      if (roomId) {
        getMessages();
      }
    }, [roomId, getMessages])
  );

  // Refetch messages on app resume (fixes stale state after unlock).
  useEffect(() => {
    if (Platform.OS === 'web' || !roomId) {
      return;
    }
    const handleAppStateChange = (nextAppState: AppStateStatus) => {
      if (
        appState.current.match(/inactive|background/) &&
        nextAppState === 'active'
      ) {
        setLoadingMessages(true);
        getMessages();
      }
      appState.current = nextAppState;
      log('AppState ChatRoom', appState.current);
    };
    const subscription = AppState.addEventListener(
      'change',
      handleAppStateChange
    );
    return () => {
      subscription.remove();
    };
  }, [getMessages, roomId]);

  // mark messages as viewed after successful refresh
  useEffect(() => {
    if (room.messages && user.unreadMessageCount > 0) {
      trackViewedMessages();
    }
    setLoadingMessages(false);
  }, [room.messages, user.unreadMessageCount, trackViewedMessages]);

  // notify when successfully tracked message views to refresh user context
  useEffect(() => {
    if (room.trackedViewedMessages) {
      onTrackViewedMessagesRef.current?.();
    }
  }, [room.trackedViewedMessages]);

  useEffect(
    () => () => {
      if (submitFocusTimeoutRef.current) {
        clearTimeout(submitFocusTimeoutRef.current);
      }
    },
    []
  );

  const muteOutsideHourNotification = useMemo(
    () =>
      room.messages.some(
        message =>
          message.body.isOutsideHoursNotification &&
          differenceInMinutes(new Date(), message.created) < 60
      ),
    [room]
  );

  useEffect(() => {
    if (!text) {
      lastAppliedInputHeightRef.current = textFieldDefaultHeight;
      setTextInputHeight(textFieldDefaultHeight);
    }
  }, [text]);

  const handleSubmit = () => {
    if (!text) return;
    postMessage('text', {
      text,
      muteOutsideHourNotification
    });
    setText('');
    if (submitFocusTimeoutRef.current) {
      clearTimeout(submitFocusTimeoutRef.current);
    }
    submitFocusTimeoutRef.current = setTimeout(() => {
      submitFocusTimeoutRef.current = undefined;
      (textInputRef.current as any)?.focus?.();
    }, 300);
  };
  // Import to check length in useMemo dependency array so that the messages
  // get refreshed when new messages are added.
  const length = room.messages.length;
  const messages = useMemo(
    () => (length ? room.messages.sort((a, b) => b.id - a.id) : room.messages),
    [length, room.messages]
  );

  const [isKeyboardVisible, setKeyboardVisible] = useState(false);

  // Need to create state first. Setter is not used in this case
  const [marginBottom] = useState(new Animated.Value(0));
  // get safe area inset for identifying margin padding for showing text input
  // above keyboard for iOS
  const insets = useSafeAreaInsets();

  // detect when keyboard is open/closed to add padding for text input display
  // above the keyboard.
  useEffect(() => {
    const keyboardDidShowListener = Keyboard.addListener(
      'keyboardWillShow',
      () => {
        setKeyboardVisible(true); // or some other action
        Animated.timing(marginBottom, {
          toValue: 80 + insets.bottom,
          duration: 150,
          useNativeDriver: false
        }).start(); // < Don't forget to start!
      }
    );
    const keyboardDidHideListener = Keyboard.addListener(
      'keyboardWillHide',
      () => {
        setKeyboardVisible(false); // or some other action
        Animated.timing(marginBottom, {
          toValue: 0,
          duration: 150,
          useNativeDriver: false
        }).start(); // < Don't forget to start!
      }
    );

    return () => {
      keyboardDidHideListener.remove();
      keyboardDidShowListener.remove();
    };
  }, []);

  const bottomTabOffset = useSafeTabBarHeight();
  const charCounterColor =
    text.length >= chatMessageMaxChars
      ? Colors.errorCaption
      : text.length >= chatMessageMaxChars * 0.9
        ? Colors.featuredAlertCardColor
        : '#999';

  return (
    <StyledContainer
      behavior='padding'
      isKeyboardVisible={isKeyboardVisible}
      keyboardVerticalOffset={
        Platform.OS === 'android' ? bottomTabOffset + 16 : undefined
      }
    >
      <Animated.View
        style={{
          flexBasis: 'auto',
          flexGrow: 1,
          flexShrink: 1,
          marginBottom
        }}
      >
        <StyledFlatList
          inverted // invert the scroll so that list starts at bottom: ;
          ref={(flatList: FlatList) => {
            flatListRef.current = flatList;
          }}
          data={messages}
          keyExtractor={item => item.id.toString()}
          renderItem={({ item }) => {
            const isOtherUser =
              item.isAiGenerated || user?.id !== item.createdBy?.id;
            return (
              <StyledListItem
                testID={item.body?.text || 'chat-emptyMessage-label'}
                left={isOtherUser}
              >
                {isOtherUser && (
                  <ListItemIcon>
                    <ProfileButtonMenu
                      isAdmin={isOtherUser}
                      name={
                        item.createdBy?.firstName ||
                        (item.isAiGenerated ? 'AI Assistant' : 'Unknown')
                      }
                    />
                  </ListItemIcon>
                )}
                <View>
                  {embedded && isOtherUser && (
                    <StyledChatNameText>
                      {item.isAiGenerated
                        ? 'AI Assistant'
                        : `${item.createdBy?.firstName || ''} ${
                            item.createdBy?.lastName || ''
                          }`.trim()}
                    </StyledChatNameText>
                  )}
                  <StyledMessageView rightAlign={!isOtherUser}>
                    {(() => {
                      switch (item.type) {
                        default:
                          return (
                            <ListItemChatBubble
                              left={isOtherUser}
                              imageUrl={item.body?.imageUrl}
                              videoUrl={item.body?.videoUrl}
                              text={item.body?.text || ''}
                              embedded={embedded}
                              isAiGenerated={item.isAiGenerated}
                            />
                          );
                      }
                    })()}
                    <StyledDateText>
                      {/* message.created can be empty in simulator */}
                      {item.created
                        ? format(item.created, 'MMM d, yyyy h:mm a')
                        : ''}
                    </StyledDateText>
                  </StyledMessageView>
                </View>
              </StyledListItem>
            );
          }}
          // NOTE: scrollToEnd is janky for long lists per documentation. Instead
          // we use the `inverted` props for FlatList (see above)
          // onContentSizeChange={() => {
          //   setTimeout(() => {
          //     flatListRef.current?.scrollToEnd({ animated: false });
          //   }, 1000);
          // }}
        />
        {archived && (
          <StyledArchiveTextContainer>
            <Caption color='dark' style={{ textAlign: 'center' }}>
              {canPost
                ? 'This chat has been archived for the participant.'
                : 'This chat has been archived.'}
            </Caption>
          </StyledArchiveTextContainer>
        )}
        {canPost ? (
          <StyledComposerWrap>
            <StyledTextInputContainer>
              <StyledTextInput
                testID={elementIDs.chatTextFieldID}
                ref={(textInput: TextInput) => {
                  textInputRef.current = textInput;
                }}
                multiline
                textAlign='left'
                placeholder='Send Message'
                maxLength={chatMessageMaxChars}
                value={text}
                onChangeText={setText}
                onSubmitEditing={handleSubmit}
                onContentSizeChange={e => {
                  const raw = e.nativeEvent.contentSize.height;
                  const measured = Platform.OS === 'web' ? raw : raw + 24;
                  const next = Math.max(
                    textFieldDefaultHeight,
                    Math.ceil(measured)
                  );
                  if (Math.abs(next - lastAppliedInputHeightRef.current) < 2) {
                    return;
                  }
                  lastAppliedInputHeightRef.current = next;
                  setTextInputHeight(next);
                }}
                style={{
                  height: Math.max(44, textInputHeight),
                  borderRadius:
                    textInputHeight <= textFieldDefaultHeight ? 100 : 20
                }}
              />
              <StyledIconButton
                testID={elementIDs.chatSendButtonID}
                size='normal'
                disabled={room.isPosting || room.isLoading}
                onPress={() => {
                  if (text.length > 0) {
                    handleSubmit();
                  } else {
                    setOpenImageActionSheet(true);
                  }
                }}
              >
                {room.isPosting ? (
                  <ActivityIndicator />
                ) : text.length > 0 ? (
                  <Feather name='send' size={24} color={Colors.primary} />
                ) : (
                  <Feather name='camera' size={24} color={Colors.primary} />
                )}
              </StyledIconButton>
            </StyledTextInputContainer>
            <StyledComposerFooter>
              <SubCaption
                color={charCounterColor}
                style={{ alignSelf: 'flex-end' }}
              >
                {`${text.length} / ${chatMessageMaxChars}`}
              </SubCaption>
              {text.length >= chatMessageMaxChars ? (
                <StyledLimitHint
                  accessibilityLiveRegion='polite'
                  color={Colors.errorCaption}
                >
                  Maximum message length reached.
                </StyledLimitHint>
              ) : null}
            </StyledComposerFooter>
          </StyledComposerWrap>
        ) : null}
      </Animated.View>
      <ImageActionSheet
        visible={openImageActionSheet}
        onClose={() => setOpenImageActionSheet(false)}
        resize={[1024]}
        onUploadedImages={uploadedFiles => {
          if (uploadedFiles.length > 0) {
            postMessage('text', {
              text: uploadedFiles[0]?.file.name,
              imageUrl: uploadedFiles[0]?.presignedResult?.destinationUrl,
              muteOutsideHourNotification
            });
            setOpenImageActionSheet(false);
          }
        }}
        onUploadVideos={uploadedFiles => {
          if (uploadedFiles.length > 0) {
            postMessage('text', {
              text: uploadedFiles[0]?.file.name,
              videoUrl: uploadedFiles[0]?.presignedResult?.destinationUrl,
              muteOutsideHourNotification
            });
            setOpenImageActionSheet(false);
          }
        }}
        onUploadError={error => {
          log('ERROR', error.message);
          setOpenImageActionSheet(false);
        }}
        uploadPath='files/presign/user-message'
        pickerOptions={{
          mediaTypes: ['images', 'videos'],
          allowsEditing: false,
          quality: 1
        }}
        disableCameraCapture={embedded}
      />
      {loadingMessages && <ActivityIndicatorScreen modal />}
    </StyledContainer>
  );
};

export default ChatRoom;
